import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import GuardianAuth from '@amzn/GuardianAuth/lib/auth/GuardianAuth';
import ToastErrorMessage from '@amzn/GuardianWidgetCommons/lib/components/ToastErrorMessage';
import asyncRequest from '@amzn/GuardianWidgetCommons/lib/helper/asyncRequest';
import React from 'react';
import { toast } from 'react-toastify';
import { AppThunk } from '../../context/store';
import { GET_ONSITE_REPONDERS_ENDPOINT } from '../../lib/globals';
import { CARDHOLDER_API_RESPONSE_SCHEMA, PII_MASK } from '../../lib/constants';
import { getResponderBadgetimeAndEtString } from '../../lib/helpers/helpers';
import { DateTime } from 'luxon';

const {
    FIRST_NAME,
    LAST_NAME,
    TITLE,
    LOGIN,
    EMPID,
    IMG_URL,
    BADGE_COLOR,
    MANAGER,
    MANAGER_FIRST_NAME,
    MANAGER_LAST_NAME,
    DEVICE_NAME,
    EVENT_TIMESTAMP,
    PUNCH_EVENT_TIMESTAMP,
    JOB_LEVEL,
    MOBILE_PHONE,
    IS_MOBILE_PHONE_PRIVATE,
    IMT_ROLE,
    IMT_IS_PRIMARY,
    IMT_LOCKED
} = CARDHOLDER_API_RESPONSE_SCHEMA;

const { doRequest } = asyncRequest;

export interface responderType {
    sites: string[];
    firstName: string;
    lastName: string;
    jobLevel: string;
    title: string;
    login: string;
    empId: string;
    imgURL: string;
    badgeColor: string;
    manager: string;
    managerFirstName: string;
    managerLastName: string;
    deviceName: string;
    eventTimestamp: number;
    mobilePhone: string | null;
    imtRole: string;
    imtIsPrimary: number;
    imtLocked: number;
}

export interface availableResponders {
    firstResponders: { [empId: string]: responderType };
    firstAiders: { [empId: string]: responderType };
    fireMarshals: { [empId: string]: responderType };
    imt: { [empId: string]: responderType };
    whs: { [empId: string]: responderType };
    firstResponderCount: number | null;
    firstAidersCount: number | null;
    fireMarshalCount: number | null;
    imtCount: number | null;
    whsCount: number | null;
}

interface ResponderDashboardState {
    isGettingResponderDashboard: boolean;
    isRefreshingResponderDashboard: boolean;
    cardholders: any;
    responders: availableResponders | null;
    csvString: string | null;
    filteredResponders: availableResponders | null;
    lastRefresh: number;
}

export const initialState: ResponderDashboardState = {
    isGettingResponderDashboard: false,
    isRefreshingResponderDashboard: false,
    cardholders: null,
    responders: null,
    csvString: null,
    filteredResponders: null,
    lastRefresh: 0
};

const responderDashboardSlice = createSlice({
    name: 'responderDashboard',
    initialState,
    reducers: {
        resetOnsiteResponders(state) {
            state.cardholders = null;
            state.responders = null;
        },
        resetCsvString(state) {
            state.csvString = null;
        },
        startGetOnsiteResponders(state) {
            state.isGettingResponderDashboard = true;
        },
        startRefreshOnsiteResponders(state) {
            state.isRefreshingResponderDashboard = true;
        },
        getOnsiteRespondersSucceeded(state, action: PayloadAction<any>) {
            const { cardholders, responders } = action.payload;
            state.cardholders = cardholders;
            state.responders =
                !responders.firstResponderCount &&
                !responders.firstAidersCount &&
                !responders.fireMarshalCount &&
                !responders.imtCount &&
                !responders.whsCount
                    ? null
                    : responders;
            state.isGettingResponderDashboard = false;
            state.isRefreshingResponderDashboard = false;
            state.lastRefresh = Date.now();
        },
        exportOnsiteRespondersSucceeded(state, action: PayloadAction<any>) {
            state.csvString = action.payload;
        },
        finishGetOnsiteResponders(state) {
            state.isGettingResponderDashboard = false;
            state.isRefreshingResponderDashboard = false;
        },
        setFilteredResponders(state, action: PayloadAction<any>) {
            const { filteredResponders } = action.payload;
            state.filteredResponders = filteredResponders;
        }
    }
});

export const {
    resetOnsiteResponders,
    resetCsvString,
    startGetOnsiteResponders,
    startRefreshOnsiteResponders,
    getOnsiteRespondersSucceeded,
    exportOnsiteRespondersSucceeded,
    finishGetOnsiteResponders,
    setFilteredResponders
} = responderDashboardSlice.actions;

export default responderDashboardSlice.reducer;

const getMaskedOrFormattedTimestamp = (timestamp: string | number): string => {
    return timestamp === PII_MASK ? PII_MASK : getResponderBadgetimeAndEtString(timestamp);
};

export const getOnsiteResponders = (siteNames: string[], isRefresh = false): AppThunk => async (dispatch) => {
    try {
        if (!siteNames.length) {
            dispatch(resetOnsiteResponders());
        } else {
            isRefresh ? dispatch(startRefreshOnsiteResponders()) : dispatch(startGetOnsiteResponders());
            const request = { siteNames };

            const { data } = await doRequest(
                GET_ONSITE_REPONDERS_ENDPOINT,
                request,
                GuardianAuth.createRequestAuthHeader()
            );

            if (data) {
                const siteKeys = Object.keys(data);
                let cardholderData: any = {};
                const combinedFirstResponders: { [empId: string]: any } = {};
                const combinedFirstAiders: { [empId: string]: any } = {};
                const combinedFireMarshals: { [empId: string]: any } = {};
                const combinedImt: { [empId: string]: any } = {};
                const combinedWhs: { [empId: string]: any } = {};
                let firstResponderCount: number | null = null;
                let firstAidersCount: number | null = null;
                let fireMarshalCount: number | null = null;
                let imtCount: number | null = null;
                let whsCount: number | null = null;

                siteKeys.forEach((siteName) => {
                    const { cardholders, firstResponders, firstAiders, fireMarshals, imt, whs } = data[
                        siteName
                    ];
                    cardholderData = {
                        ...cardholderData,
                        ...cardholders
                    };

                    if (firstResponders) {
                        if (!firstResponderCount) {
                            firstResponderCount = firstResponders.length;
                        } else {
                            firstResponderCount += firstResponders.length;
                        }

                        firstResponders.forEach((empId: string) => {
                            if (combinedFirstResponders[empId]) {
                                combinedFirstResponders[empId].sites.push(siteName);
                            } else {
                                combinedFirstResponders[empId] = {
                                    sites: [siteName],
                                    [EMPID]: cardholders[empId][EMPID],
                                    [FIRST_NAME]: cardholders[empId][FIRST_NAME],
                                    [LAST_NAME]: cardholders[empId][LAST_NAME],
                                    [JOB_LEVEL]: cardholders[empId][JOB_LEVEL],
                                    [TITLE]: cardholders[empId][TITLE],
                                    [LOGIN]: cardholders[empId][LOGIN],
                                    [IMG_URL]: cardholders[empId][IMG_URL],
                                    [MANAGER]: cardholders[empId][MANAGER],
                                    [MANAGER_FIRST_NAME]: cardholders[empId][MANAGER_FIRST_NAME],
                                    [MANAGER_LAST_NAME]: cardholders[empId][MANAGER_LAST_NAME],
                                    [BADGE_COLOR]: cardholders[empId][BADGE_COLOR],
                                    [DEVICE_NAME]: cardholders[empId][DEVICE_NAME],
                                    [EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][EVENT_TIMESTAMP]
                                    ),
                                    [PUNCH_EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][PUNCH_EVENT_TIMESTAMP]
                                    ),
                                    [MOBILE_PHONE]: cardholders[empId][MOBILE_PHONE],
                                    [IS_MOBILE_PHONE_PRIVATE]: cardholders[empId][IS_MOBILE_PHONE_PRIVATE]
                                };
                            }
                        });
                    }

                    if (imt) {
                        if (!imtCount) {
                            imtCount = imt.length;
                        } else {
                            imtCount += imt.length;
                        }

                        imt.forEach((empId: string) => {
                            if (combinedImt[empId]) {
                                combinedImt[empId].sites.push(siteName);
                            } else {
                                combinedImt[empId] = {
                                    sites: [siteName],
                                    [EMPID]: cardholders[empId][EMPID],
                                    [FIRST_NAME]: cardholders[empId][FIRST_NAME],
                                    [LAST_NAME]: cardholders[empId][LAST_NAME],
                                    [JOB_LEVEL]: cardholders[empId][JOB_LEVEL],
                                    [TITLE]: cardholders[empId][TITLE],
                                    [LOGIN]: cardholders[empId][LOGIN],
                                    [IMG_URL]: cardholders[empId][IMG_URL],
                                    [MANAGER]: cardholders[empId][MANAGER],
                                    [MANAGER_FIRST_NAME]: cardholders[empId][MANAGER_FIRST_NAME],
                                    [MANAGER_LAST_NAME]: cardholders[empId][MANAGER_LAST_NAME],
                                    [BADGE_COLOR]: cardholders[empId][BADGE_COLOR],
                                    [DEVICE_NAME]: cardholders[empId][DEVICE_NAME],
                                    [EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][EVENT_TIMESTAMP]
                                    ),
                                    [PUNCH_EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][PUNCH_EVENT_TIMESTAMP]
                                    ),
                                    [MOBILE_PHONE]: cardholders[empId][MOBILE_PHONE],
                                    [IS_MOBILE_PHONE_PRIVATE]: cardholders[empId][IS_MOBILE_PHONE_PRIVATE],
                                    [IMT_ROLE]: cardholders[empId][IMT_ROLE]?.toLowerCase() || ''
                                };
                            }
                        });
                    }

                    if (whs) {
                        if (!whsCount) {
                            whsCount = whs.length;
                        } else {
                            whsCount += whs.length;
                        }

                        whs.forEach((empId: string) => {
                            if (combinedWhs[empId]) {
                                combinedWhs[empId].sites.push(siteName);
                            } else {
                                combinedWhs[empId] = {
                                    sites: [siteName],
                                    [EMPID]: cardholders[empId][EMPID],
                                    [FIRST_NAME]: cardholders[empId][FIRST_NAME],
                                    [LAST_NAME]: cardholders[empId][LAST_NAME],
                                    [JOB_LEVEL]: cardholders[empId][JOB_LEVEL],
                                    [TITLE]: cardholders[empId][TITLE],
                                    [LOGIN]: cardholders[empId][LOGIN],
                                    [IMG_URL]: cardholders[empId][IMG_URL],
                                    [MANAGER]: cardholders[empId][MANAGER],
                                    [MANAGER_FIRST_NAME]: cardholders[empId][MANAGER_FIRST_NAME],
                                    [MANAGER_LAST_NAME]: cardholders[empId][MANAGER_LAST_NAME],
                                    [BADGE_COLOR]: cardholders[empId][BADGE_COLOR],
                                    [DEVICE_NAME]: cardholders[empId][DEVICE_NAME],
                                    [EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][EVENT_TIMESTAMP]
                                    ),
                                    [PUNCH_EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][PUNCH_EVENT_TIMESTAMP]
                                    ),
                                    [MOBILE_PHONE]: cardholders[empId][MOBILE_PHONE],
                                    [IS_MOBILE_PHONE_PRIVATE]: cardholders[empId][IS_MOBILE_PHONE_PRIVATE]
                                };
                            }
                        });
                    }

                    if (firstAiders) {
                        if (!firstAidersCount) {
                            firstAidersCount = firstAiders.length;
                        } else {
                            firstAidersCount += firstAiders.length;
                        }

                        firstAiders.forEach((empId: string) => {
                            if (combinedFirstAiders[empId]) {
                                combinedFirstAiders[empId].sites.push(siteName);
                            } else {
                                combinedFirstAiders[empId] = {
                                    sites: [siteName],
                                    [EMPID]: cardholders[empId][EMPID],
                                    [FIRST_NAME]: cardholders[empId][FIRST_NAME],
                                    [LAST_NAME]: cardholders[empId][LAST_NAME],
                                    [JOB_LEVEL]: cardholders[empId][JOB_LEVEL],
                                    [TITLE]: cardholders[empId][TITLE],
                                    [LOGIN]: cardholders[empId][LOGIN],
                                    [IMG_URL]: cardholders[empId][IMG_URL],
                                    [MANAGER]: cardholders[empId][MANAGER],
                                    [MANAGER_FIRST_NAME]: cardholders[empId][MANAGER_FIRST_NAME],
                                    [MANAGER_LAST_NAME]: cardholders[empId][MANAGER_LAST_NAME],
                                    [BADGE_COLOR]: cardholders[empId][BADGE_COLOR],
                                    [DEVICE_NAME]: cardholders[empId][DEVICE_NAME],
                                    [EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][EVENT_TIMESTAMP]
                                    ),
                                    [PUNCH_EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][PUNCH_EVENT_TIMESTAMP]
                                    ),
                                    [MOBILE_PHONE]: cardholders[empId][MOBILE_PHONE],
                                    [IS_MOBILE_PHONE_PRIVATE]: cardholders[empId][IS_MOBILE_PHONE_PRIVATE]
                                };
                            }
                        });
                    }

                    if (fireMarshals) {
                        if (!fireMarshalCount) {
                            fireMarshalCount = fireMarshals.length;
                        } else {
                            fireMarshalCount += fireMarshals.length;
                        }

                        fireMarshals.forEach((empId: string) => {
                            if (combinedFireMarshals[empId]) {
                                combinedFireMarshals[empId].sites.push(siteName);
                            } else {
                                combinedFireMarshals[empId] = {
                                    sites: [siteName],
                                    [EMPID]: cardholders[empId][EMPID],
                                    [FIRST_NAME]: cardholders[empId][FIRST_NAME],
                                    [LAST_NAME]: cardholders[empId][LAST_NAME],
                                    [JOB_LEVEL]: cardholders[empId][JOB_LEVEL],
                                    [TITLE]: cardholders[empId][TITLE],
                                    [LOGIN]: cardholders[empId][LOGIN],
                                    [IMG_URL]: cardholders[empId][IMG_URL],
                                    [MANAGER]: cardholders[empId][MANAGER],
                                    [MANAGER_FIRST_NAME]: cardholders[empId][MANAGER_FIRST_NAME],
                                    [MANAGER_LAST_NAME]: cardholders[empId][MANAGER_LAST_NAME],
                                    [BADGE_COLOR]: cardholders[empId][BADGE_COLOR],
                                    [DEVICE_NAME]: cardholders[empId][DEVICE_NAME],
                                    [EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][EVENT_TIMESTAMP]
                                    ),
                                    [PUNCH_EVENT_TIMESTAMP]: getMaskedOrFormattedTimestamp(
                                        cardholders[empId][PUNCH_EVENT_TIMESTAMP]
                                    ),
                                    [MOBILE_PHONE]: cardholders[empId][MOBILE_PHONE],
                                    [IS_MOBILE_PHONE_PRIVATE]: cardholders[empId][IS_MOBILE_PHONE_PRIVATE]
                                };
                            }
                        });
                    }
                });

                dispatch(
                    getOnsiteRespondersSucceeded({
                        cardholders: cardholderData,
                        responders: {
                            firstResponders: combinedFirstResponders,
                            firstAiders: combinedFirstAiders,
                            fireMarshals: combinedFireMarshals,
                            imt: combinedImt,
                            whs: combinedWhs,
                            firstResponderCount,
                            firstAidersCount,
                            fireMarshalCount,
                            imtCount,
                            whsCount
                        }
                    })
                );
            }
        }
    } catch (error) {
        toast.error(
            <ToastErrorMessage header={'GetOnsiteResponders request failed.'} errorMessage={error.message} />
        );
    }
};

export const exportOnsiteResponders = (
    siteNames: string[],
    exportOptions: { [key: string]: string } | null = null
): AppThunk => async (dispatch) => {
    try {
        const request: Record<string, any> = {
            siteNames,
            exportCsv: exportOptions || {},
            timeZone: DateTime.local().zoneName
        };

        const { data } = await doRequest(
            GET_ONSITE_REPONDERS_ENDPOINT,
            request,
            GuardianAuth.createRequestAuthHeader()
        );

        if (data) {
            dispatch(exportOnsiteRespondersSucceeded(data));
        }
    } catch (error) {
        toast.error(<ToastErrorMessage header={'Export request failed.'} errorMessage={error.message} />);
    }
};

export const clearCsvString = (): AppThunk => async (dispatch) => {
    dispatch(resetCsvString());
};

export const filterResponders = (responders: any, searchValue: string): AppThunk => async (dispatch) => {
    const filterededFirstResponders: { [empId: string]: any } = {};
    const filteredFirstAiders: { [empId: string]: any } = {};
    const filteredFireMarshals: { [empId: string]: any } = {};
    const filteredImt: { [empId: string]: any } = {};
    const filterededWhs: { [empId: string]: any } = {};
    let filteredFirstResponderCount: number | null = null;
    let filteredFirstAidersCount: number | null = null;
    let filteredFireMarshalCount: number | null = null;
    let filteredImtCount: number | null = null;
    let filteredWhsCount: number | null = null;

    if (responders?.firstResponders) {
        Object.keys(responders.firstResponders).forEach((empId: string) => {
            const responder = responders.firstResponders[empId] as responderType;
            if (
                //@ts-ignore
                responder[FIRST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LAST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LOGIN].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                `l${responder[JOB_LEVEL]}`.toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[TITLE].toLowerCase().includes(searchValue.toLowerCase()) ||
                responder.sites
                    .join()
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            ) {
                filterededFirstResponders[empId] = responder;
            }
        });
        filteredFirstResponderCount = Object.keys(filterededFirstResponders).length;
    }

    if (responders?.imt) {
        Object.keys(responders.imt).forEach((empId: string) => {
            const responder = responders.imt[empId] as responderType;
            if (
                //@ts-ignore
                responder[FIRST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LAST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LOGIN].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                `l${responder[JOB_LEVEL]}`.toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[TITLE].toLowerCase().includes(searchValue.toLowerCase()) ||
                responder.sites
                    .join()
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            ) {
                filteredImt[empId] = responder;
            }
        });
        filteredImtCount = Object.keys(filteredImt).length;
    }

    if (responders?.whs) {
        Object.keys(responders.whs).forEach((empId: string) => {
            const responder = responders.whs[empId] as responderType;
            if (
                //@ts-ignore
                responder[FIRST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LAST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LOGIN].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                `l${responder[JOB_LEVEL]}`.toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[TITLE].toLowerCase().includes(searchValue.toLowerCase()) ||
                responder.sites
                    .join()
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            ) {
                filterededWhs[empId] = responder;
            }
        });
        filteredWhsCount = Object.keys(filterededWhs).length;
    }

    if (responders?.firstAiders) {
        Object.keys(responders.firstAiders).forEach((empId: string) => {
            const responder = responders.firstAiders[empId] as responderType;
            if (
                //@ts-ignore
                responder[FIRST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LAST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LOGIN].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                `l${responder[JOB_LEVEL]}`.toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[TITLE].toLowerCase().includes(searchValue.toLowerCase()) ||
                responder.sites
                    .join()
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            ) {
                filteredFirstAiders[empId] = responder;
            }
        });
        filteredFirstAidersCount = Object.keys(filteredFirstAiders).length;
    }

    if (responders?.fireMarshals) {
        Object.keys(responders.fireMarshals).forEach((empId: string) => {
            const responder = responders.fireMarshals[empId] as responderType;
            if (
                //@ts-ignore
                responder[FIRST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LAST_NAME].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[LOGIN].toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                `l${responder[JOB_LEVEL]}`.toLowerCase().includes(searchValue.toLowerCase()) ||
                //@ts-ignore
                responder[TITLE].toLowerCase().includes(searchValue.toLowerCase()) ||
                responder.sites
                    .join()
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            ) {
                filteredFireMarshals[empId] = responder;
            }
        });
        filteredFireMarshalCount = Object.keys(filteredFireMarshals).length;
    }

    dispatch(
        setFilteredResponders({
            filteredResponders: {
                firstResponders: filterededFirstResponders,
                firstAiders: filteredFirstAiders,
                fireMarshals: filteredFireMarshals,
                imt: filteredImt,
                whs: filterededWhs,
                firstResponderCount: filteredFirstResponderCount,
                firstAidersCount: filteredFirstAidersCount,
                fireMarshalCount: filteredFireMarshalCount,
                imtCount: filteredImtCount,
                whsCount: filteredWhsCount
            }
        })
    );
};

export const clearFilteredResponders = (): AppThunk => async (dispatch) => {
    dispatch(
        setFilteredResponders({
            filteredResponders: null
        })
    );
};
